import React, { FunctionComponent, MouseEvent, useEffect, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { MoveablePassableProps } from "@multiweb/picasso";
import { Icon, IconColor, IconSize } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState, NoneOwnProps } from "@multiweb/types/react";
import { createLoadActivityHeaderAction } from "../actions";
import { PropagationStopper } from "../components/dialogs/components";
import { ActivityEditDialog } from "../components/dialogs/editDialogs";
import { LOC_INHERIT_PARTICIPANTS, LOC_KILL_CHILDREN, LOC_ONGOING_ACTIVITY, LOC_PERTINENCE_TO_SETS, LOC_TYPE_FOR_JOIN } from "../locKeys";
import { getActivityByIndex } from "../selectors";
import { IActivity } from "../types";
import { ActivityType, IOTransitionType } from "../workflowModel";
import { getWfStateCodes } from "./utils";

const classNames = bemClassNames("activity-box");
const infoSectionStyle = classNames("info-section");
const iconsSectionStyle = classNames("icons-section");
const headerStyle = classNames("header");

type ActivityComponentProps = {
    readonly activity: IActivity;
    readonly pertinenceToSetsText: string;
    readonly killChildrenText: string;
    readonly onGoingText: string;
    readonly inheritParticipantsText: string;
    readonly typeForJoinText: string;
    readonly loadActivityHeader: (hostKey: string, picassoIndex: number, attributeName: readonly string[]) => void;
} & Pick<MoveablePassableProps, "picassoInstanceKey" | "isSelected" | "clickSelectComponent" | "mouseDownComponentSelect">;

const ActivityComponent: FunctionComponent<ActivityComponentProps> = ({
    activity,
    isSelected,
    clickSelectComponent,
    mouseDownComponentSelect,
    loadActivityHeader,
    pertinenceToSetsText,
    killChildrenText,
    onGoingText,
    inheritParticipantsText,
    typeForJoinText,
    picassoInstanceKey,
}) => {
    const [isEditDialogShown, setIsEditDialogShown] = useState(false);

    const isStartActivity = activity.type === ActivityType.start;
    const isEndActivity = activity.type === ActivityType.end;
    const isActivityInMoreSets = activity.positions.length > 1;
    const isActivityContinuos = activity.triggers.length === 0;
    const isAndTransType = activity.inputTransType === IOTransitionType.and;
    const inheritsParticipants = activity.inheritParticipants;
    const killChildren = activity.killChildren;
    const hasCondition = !!activity.condition;
    const hasRunCondition = !!activity.runCondition; // Online podmínka
    const isActive = activity.isActive;
    const pertienceToSets = `${pertinenceToSetsText}:\n${activity.positions.map(aa => aa.setName).join("\n")}`;

    useEffect(() => {
        const wfStateCodes = getWfStateCodes(activity.actions);
        if (wfStateCodes.length)
            loadActivityHeader(picassoInstanceKey, activity.picassoIndex, wfStateCodes);
    }, [activity.actions, activity.picassoIndex, picassoInstanceKey, loadActivityHeader]);

    const onDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
        if (!e.ctrlKey && isSelected) // Zabránění případu otevření editačního dialogu při hromadném výběru...
            setIsEditDialogShown(true);
    };

    const onCloseEdit = () => {
        setIsEditDialogShown(false);
    };

    return (
        <div
            className={classNames({
                "selected": isSelected,
                "active": isActive,
                "ended": isEndActivity && isActive,
                "condition": hasCondition,
                "runcondition": hasRunCondition,
                "start-type": isStartActivity,
                "end-type": isEndActivity,
            })}
            onClick={clickSelectComponent}
            onMouseDown={mouseDownComponentSelect}
            title={activity.description}
            {...{ onDoubleClick }} >
            {!!activity.header && <span className={headerStyle} title={activity.header}>{activity.header}</span>}
            <div className={infoSectionStyle}>
                <p className={classNames("text", { "normal-type": activity.type === ActivityType.normal })}>{activity.name}</p>
                {isActivityInMoreSets && <Icon iconCode={IconCode.docPaperStack} color={IconColor.orange} size={IconSize.small} title={pertienceToSets} />}
            </div>
            <div className={iconsSectionStyle}>
                {killChildren && <Icon iconCode={IconCode.uiCloseParallelConnection} size={IconSize.small} title={killChildrenText} />}
                {isActivityContinuos && <Icon iconCode={IconCode.uiArrowRight} size={IconSize.small} title={onGoingText} />}
                {inheritsParticipants && <Icon iconCode={IconCode.userGroup} size={IconSize.small} title={inheritParticipantsText} />}
                {isAndTransType && <Icon iconCode={IconCode.uiPropertyAnd} size={IconSize.small} title={typeForJoinText} />}
            </div>
            {isEditDialogShown &&
                <PropagationStopper>
                    <ActivityEditDialog hostKey={picassoInstanceKey} picassoIndex={activity.picassoIndex} {...{ onCloseEdit }} />
                </PropagationStopper>
            }
        </div>
    );
};

type StateProps = Pick<ActivityComponentProps, "activity" | "pertinenceToSetsText" | "killChildrenText" | "onGoingText" | "inheritParticipantsText" | "typeForJoinText">;

const mapStateToProps: MapStateToProps<StateProps, MoveablePassableProps, IAppState> = (state, { picassoInstanceKey, uiComponent }) => ({
    activity: getActivityByIndex(state, picassoInstanceKey, uiComponent.index),
    pertinenceToSetsText: getPrimaryText(state, LOC_PERTINENCE_TO_SETS) ?? "Příslušnost k sadám",
    killChildrenText: getPrimaryText(state, LOC_KILL_CHILDREN) ?? "Zavření paralelní aktivity",
    onGoingText: getPrimaryText(state, LOC_ONGOING_ACTIVITY) ?? "Průběžná aktivita",
    inheritParticipantsText: getPrimaryText(state, LOC_INHERIT_PARTICIPANTS) ?? "Dědit hráče",
    typeForJoinText: getPrimaryText(state, LOC_TYPE_FOR_JOIN) ?? "Typ spojení",
});

type DispatchProps = Pick<ActivityComponentProps, "loadActivityHeader">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, NoneOwnProps> = {
    loadActivityHeader: createLoadActivityHeaderAction,
};

export const Activity = connect(mapStateToProps, mapDispatchToProps)(ActivityComponent);
